import React, { useEffect, useState } from "react";
import { Helmet } from 'react-helmet-async';
import { Link } from "react-router-dom";
import { ArrowLeft, Bell, Mail, Package, Gift, Megaphone, Clapperboard } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import UserSidebar from "@/components/UserSidebar";
import { useAuth } from "@/components/AuthContext";
import {
  fetchNotificationSettings,
  updateNotificationSettings,
  updateMySettings,
  type NotificationSettings
} from "@/services/customerService";

const SettingsPage: React.FC = () => {
  const { currentUser, refreshUser } = useAuth();
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings | null>(null);
  const [lotteryAnimationEnabled, setLotteryAnimationEnabled] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotificationSettings();
  }, []);

  useEffect(() => {
    if (currentUser?.lottery_animation_enabled !== undefined) {
      setLotteryAnimationEnabled(currentUser.lottery_animation_enabled);
    }
  }, [currentUser?.lottery_animation_enabled]);

  const loadNotificationSettings = async () => {
    try {
      setLoading(true);
      const data = await fetchNotificationSettings();
      setNotificationSettings(data);
    } catch (err: any) {
      console.error("通知設定の取得に失敗しました", err);
      toast.error("通知設定の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationChange = async (key: keyof NotificationSettings, value: boolean) => {
    if (!notificationSettings) return;

    const previousSettings = { ...notificationSettings };
    setNotificationSettings({ ...notificationSettings, [key]: value });

    try {
      await updateNotificationSettings({ [key]: value });
      toast.success("通知設定を更新しました");
    } catch (err: any) {
      setNotificationSettings(previousSettings);
      toast.error("通知設定の更新に失敗しました");
    }
  };

  const handleLotteryAnimationChange = async (value: boolean) => {
    const previousValue = lotteryAnimationEnabled;
    setLotteryAnimationEnabled(value);

    try {
      await updateMySettings({ lottery_animation_enabled: value });
      await refreshUser();
      toast.success("演出設定を更新しました");
    } catch (err: any) {
      setLotteryAnimationEnabled(previousValue);
      toast.error("演出設定の更新に失敗しました");
    }
  };

  return (
    <>
      <Helmet>
        <title>設定</title>
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <div className="max-w-screen-lg mx-auto px-4 pb-4 sm:pb-8 pt-20">
          <div className="flex gap-8">
            {/* サイドバー - デスクトップのみ */}
            <div className="hidden lg:block">
              <UserSidebar />
            </div>

            {/* メインコンテンツ */}
            <section className="flex-1 min-w-0">
              {/* タイトルセクション */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-theme-primary border-theme rounded-theme flex items-center justify-center shadow-theme-sm">
                  <Bell className="h-5 w-5 text-theme-button" />
                </div>
                <div>
                  <h1 className="text-xl [font-weight:var(--theme-font-weight)] text-theme-bg-text">設定</h1>
                  <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-70">通知や演出の設定を管理できます</p>
                </div>
              </div>

              {/* 演出設定 */}
              <div className="bg-white border-theme rounded-theme-lg shadow-theme overflow-hidden mb-6">
                <div className="px-4 sm:px-6 py-4 border-b-[length:var(--theme-border-width)] border-b-[color:var(--theme-border-color)] bg-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                      <Clapperboard className="h-4 w-4 text-theme-button" />
                    </div>
                    <h2 className="text-lg [font-weight:var(--theme-font-weight)] text-black">演出設定</h2>
                  </div>
                  <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600 mt-1">抽選時の演出動画のON/OFFを設定できます</p>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                        <Clapperboard className="h-5 w-5 text-theme-button" />
                      </div>
                      <div>
                        <p className="[font-weight:var(--theme-font-weight)] text-black">オリパ演出動画</p>
                        <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600">抽選結果の前に演出動画を再生します</p>
                      </div>
                    </div>
                    <Switch
                      checked={lotteryAnimationEnabled}
                      onCheckedChange={handleLotteryAnimationChange}
                      className="data-[state=checked]:bg-theme-primary"
                    />
                  </div>
                </div>
              </div>

              {/* 通知設定 */}
              <div className="bg-white border-theme rounded-theme-lg shadow-theme overflow-hidden">
                <div className="px-4 sm:px-6 py-4 border-b-[length:var(--theme-border-width)] border-b-[color:var(--theme-border-color)] bg-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                      <Bell className="h-4 w-4 text-theme-button" />
                    </div>
                    <h2 className="text-lg [font-weight:var(--theme-font-weight)] text-black">通知設定</h2>
                  </div>
                  <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600 mt-1">メール通知のON/OFFを設定できます</p>
                </div>
                <div className="p-4 sm:p-6">
                  {loading ? (
                    <div className="text-center py-6">
                      <p className="[font-weight:var(--theme-font-weight-button)] text-gray-500">読み込み中...</p>
                    </div>
                  ) : notificationSettings ? (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between py-4 border-b-2 border-gray-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                            <Mail className="h-5 w-5 text-theme-button" />
                          </div>
                          <div>
                            <p className="[font-weight:var(--theme-font-weight)] text-black">ログイン通知</p>
                            <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600">新しい端末からログインした際にメールで通知します</p>
                          </div>
                        </div>
                        <Switch
                          checked={notificationSettings.login_notification}
                          onCheckedChange={(checked) => handleNotificationChange('login_notification', checked)}
                          className="data-[state=checked]:bg-theme-primary"
                        />
                      </div>

                      <div className="flex items-center justify-between py-4 border-b-2 border-gray-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                            <Package className="h-5 w-5 text-theme-button" />
                          </div>
                          <div>
                            <p className="[font-weight:var(--theme-font-weight)] text-black">配送通知</p>
                            <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600">商品の発送・配達完了時にメールで通知します</p>
                          </div>
                        </div>
                        <Switch
                          checked={notificationSettings.shipping_notification}
                          onCheckedChange={(checked) => handleNotificationChange('shipping_notification', checked)}
                          className="data-[state=checked]:bg-theme-primary"
                        />
                      </div>

                      <div className="flex items-center justify-between py-4 border-b-2 border-gray-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                            <Gift className="h-5 w-5 text-theme-button" />
                          </div>
                          <div>
                            <p className="[font-weight:var(--theme-font-weight)] text-black">抽選通知</p>
                            <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600">抽選結果や当選に関する通知をメールで受け取ります</p>
                          </div>
                        </div>
                        <Switch
                          checked={notificationSettings.lottery_notification}
                          onCheckedChange={(checked) => handleNotificationChange('lottery_notification', checked)}
                          className="data-[state=checked]:bg-theme-primary"
                        />
                      </div>

                      <div className="flex items-center justify-between py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                            <Megaphone className="h-5 w-5 text-theme-button" />
                          </div>
                          <div>
                            <p className="[font-weight:var(--theme-font-weight)] text-black">お知らせ通知</p>
                            <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600">新機能やキャンペーンのお知らせを受け取ります</p>
                          </div>
                        </div>
                        <Switch
                          checked={notificationSettings.marketing_notification}
                          onCheckedChange={(checked) => handleNotificationChange('marketing_notification', checked)}
                          className="data-[state=checked]:bg-theme-primary"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="[font-weight:var(--theme-font-weight-button)] text-gray-500">通知設定を読み込めませんでした</p>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPage;
